import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { ToasterSeverities } from "@core/enums/toaster-severities";
import { NotificationService } from "@core/services/notification.service";
import { SystemTimeService } from "@core/services/system-time.service";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Action, Store } from "@ngrx/store";
import { catchError, mergeMap, Observable, of, switchMap } from "rxjs";

import * as systemTimeActions from "../store/system-time.actions";

@Injectable()
export class SystemTimeEffects {
  loadSystemTime$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(systemTimeActions.LOAD_SYSTEM_TIME),
      switchMap((): Observable<Action> => {
        return this.systemTimeService.getSystemTime$().pipe(
          mergeMap((systemTime: Date): Observable<Action> => {
            this.store.dispatch(
              systemTimeActions.SET_SYSTEM_TIME({ systemTime })
            );
            return of();
          }),
          catchError((response: HttpErrorResponse): Observable<Action> => {
            this.notificationService.show(
              ToasterSeverities.Error,
              "SYSTEM_TIME_ERROR",
              response
            );
            return of();
          })
        );
      })
    );
  });

  loadTimeTravelIsAuthorized$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(systemTimeActions.LOAD_TIME_TRAVEL_AUTHORIZED),
      switchMap((): Observable<Action> => {
        return this.systemTimeService.isSystemTimeTravelAuthorized$().pipe(
          mergeMap((authorized: boolean): Observable<Action> => {
            this.store.dispatch(
              systemTimeActions.SET_TIME_TRAVEL_AUTHORIZED({ authorized })
            );
            return of();
          }),
          catchError((response: HttpErrorResponse): Observable<Action> => {
            this.notificationService.show(
              ToasterSeverities.Error,
              "SYSTEM_TIME_ERROR",
              response
            );
            return of();
          })
        );
      })
    );
  });

  loadTimeTravelIsActive$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(systemTimeActions.LOAD_TIME_TRAVEL_IS_ACTIVE),
      switchMap((): Observable<Action> => {
        return this.systemTimeService.isSystemTimeTravelOn$().pipe(
          mergeMap((isTimeTravelOn: boolean): Observable<Action> => {
            this.store.dispatch(
              systemTimeActions.SET_TIME_TRAVEL_IS_ACTIVE({ isTimeTravelOn })
            );
            return of();
          }),
          catchError((response: HttpErrorResponse): Observable<Action> => {
            this.notificationService.show(
              ToasterSeverities.Error,
              "SYSTEM_TIME_ERROR",
              response
            );
            return of();
          })
        );
      })
    );
  });

  updateSystemTime$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(systemTimeActions.UPDATE_SYSTEM_TIME),
      mergeMap((action: { newSystemTime: Date }): Observable<Action> => {
        return this.systemTimeService
          .updateSystemTime$(action.newSystemTime)
          .pipe(
            switchMap((): Observable<Action> => {
              this.notificationService.show(
                ToasterSeverities.Success,
                "SYSTEM_TIME_UPDATED"
              );
              return of(systemTimeActions.LOAD_SYSTEM_TIME());
            }),
            catchError((response: HttpErrorResponse): Observable<Action> => {
              this.notificationService.show(
                ToasterSeverities.Error,
                "SYSTEM_TIME_ERROR",
                response
              );
              return of();
            })
          );
      })
    );
  });

  constructor(
    private actions$: Actions,
    private systemTimeService: SystemTimeService,
    private store: Store,
    private notificationService: NotificationService
  ) {}
}
